/**
 * Fulltext scoring for the block palette and the Cmd+K command palette.
 * Pure module — diacritics-insensitive (Czech UI), prefix beats word start
 * beats substring, ties break on shorter label.
 */

export function normalize(value: string): string {
    return value
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();
}

/** 3 = prefix, 2 = word-boundary start, 1 = substring, 0 = no match. */
export function scoreLabel(query: string, label: string): number {
    const needle = normalize(query).trim();
    const haystack = normalize(label);

    if (needle === '') {
        return 1;
    }

    if (haystack.startsWith(needle)) {
        return 3;
    }

    const index = haystack.indexOf(needle);

    if (index === -1) {
        return 0;
    }

    return /[\s\-_/]/.test(haystack[index - 1] ?? '') ? 2 : 1;
}

export interface Searchable {
    label: string;
    /** Secondary match target (slug, type hint) — scored one tier lower. */
    hint?: string;
}

export function searchScore(query: string, entry: Searchable): number {
    const labelScore = scoreLabel(query, entry.label);
    const hintScore = entry.hint ? Math.max(0, scoreLabel(query, entry.hint) - 1) : 0;

    return Math.max(labelScore, hintScore);
}

/** Filter + rank; stable within equal scores (shorter label first). */
export function rankSearch<T extends Searchable>(query: string, entries: T[]): T[] {
    if (normalize(query).trim() === '') {
        return [...entries];
    }

    return entries
        .map((entry) => ({ entry, score: searchScore(query, entry) }))
        .filter((item) => item.score > 0)
        .sort((a, b) => b.score - a.score || a.entry.label.length - b.entry.label.length)
        .map((item) => item.entry);
}
